import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BkPanel from '../../Components/ui/BkPanel';
import {
  ATTRIBUTIONS,
  ATTRIBUTION_CATEGORIES,
  attributionsFor,
  type Attribution,
} from '../../config/attributions';
import { LEGAL, SUPPORT_MAILTO } from '../../config/legal';
import { TERMS_INTRO, TERMS_SECTIONS } from '../../config/terms';
import usePageMeta from '../../hooks/use-page-meta';
import LegalDocument from './LegalDocument';

function AttributionRow({ entry }: { entry: Attribution }) {
  return (
    <li className="border-b border-bk-border/60 pb-4 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {entry.href ? (
          <a
            className="font-bold text-bk-gold underline-offset-2 hover:underline"
            href={entry.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {entry.name}
          </a>
        ) : (
          <span className="font-bold text-bk-text">{entry.name}</span>
        )}
        <span className="text-xs uppercase tracking-wide text-bk-muted">
          {entry.licence}
        </span>
      </div>
      <p className="mt-1 text-sm text-bk-text/90">{entry.usedFor}</p>
      {entry.notice && (
        <p className="mt-2 border-l-2 border-bk-gold-dim/50 pl-3 text-xs italic text-bk-muted">
          {entry.notice}
        </p>
      )}
    </li>
  );
}

/** Only rendered when a third party actually requires public credit. */
function AttributionsSection() {
  if (ATTRIBUTIONS.length === 0) return null;

  const categories = ATTRIBUTION_CATEGORIES.filter(
    (category) => attributionsFor(category.id).length > 0
  );

  return (
    <section
      id="attributions"
      className="mb-8 scroll-mt-24 space-y-4"
      aria-labelledby="legal-attributions"
    >
      <div className="space-y-2 border-b border-bk-gold-dim/40 pb-2">
        <h2
          id="legal-attributions"
          className="text-lg font-bold uppercase tracking-wider text-bk-text"
        >
          Attributions
        </h2>
        <p className="text-sm text-bk-muted">
          Third-party work we are required to credit.
        </p>
      </div>

      {categories.map((category) => (
        <div key={category.id} className="space-y-3">
          <div className="space-y-1">
            <h3 className="text-base font-bold uppercase tracking-wider text-bk-text">
              {category.title}
            </h3>
            <p className="text-sm text-bk-muted">{category.blurb}</p>
          </div>
          <BkPanel>
            <ul className="space-y-4">
              {attributionsFor(category.id).map((entry) => (
                <AttributionRow key={entry.id} entry={entry} />
              ))}
            </ul>
          </BkPanel>
        </div>
      ))}
    </section>
  );
}

export default function Terms() {
  const { hash } = useLocation();
  const hasRequiredCredits = ATTRIBUTIONS.length > 0;

  usePageMeta({
    title: 'Terms of Service',
    description: `Terms of Service for ${LEGAL.productName}, including accounts, purchases, and fair play.`,
    path: '/terms',
  });

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, '');
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);

  return (
    <LegalDocument
      title="Terms of Service"
      effectiveDate={LEGAL.effectiveDate}
      intro={TERMS_INTRO}
      sections={TERMS_SECTIONS}
      tocExtras={
        hasRequiredCredits
          ? [{ id: 'attributions', title: 'Attributions' }]
          : []
      }
      after={<AttributionsSection />}
      footer={
        <>
          See also our{' '}
          <Link
            className="text-bk-gold underline-offset-2 hover:underline"
            to="/privacy"
          >
            Privacy Policy
          </Link>
          . Questions?{' '}
          <a
            className="text-bk-gold underline-offset-2 hover:underline"
            href={SUPPORT_MAILTO}
          >
            {LEGAL.supportEmail}
          </a>
          .
        </>
      }
    />
  );
}
